import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  createCategory,
  fetchCategories,
  fetchCategory,
  reorderCategories,
  updateCategory
} from "../../actions/roblox/categoriesActions";

export interface CategoryInterface {
  url: string;

  id: number;
  name: string;

  target: 'patterns' | 'accessories';

  order: number;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';

  editId: EntityId | null;
  editError: string | null;

  newParams: CategoryInterface | undefined;
  newError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<CategoryInterface>();

const slice = createSlice({
  name: 'categories',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchCategories.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.entities = action.payload.categories;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })
      // #show
      .addCase(fetchCategory.fulfilled, (state, action) => {
        const category = Object.values(action.payload.category)[0];
        state.entities[category.id] = category;
      })
      // #create
      .addCase(createCategory.fulfilled, (state, action) => {
        const category = Object.values(action.payload.category)[0];
        state.entities[category.id] = category;
        state.ids.unshift(category.id);
      })
      .addCase(createCategory.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateCategory.fulfilled, (state, action) => {
        const category = Object.values(action.payload.category)[0];
        state.entities[category.id] = category;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderCategories.fulfilled, (state, action) => {
        const { ids } = action.payload;
        state.ids = ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectIds: selectCategoryIds,
  selectEntities: selectCategoryEntities,
  selectById: selectCategoryById
} = adaptor.getSelectors<RootState>(state => state.roblox.categories);
